import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDOP, formatDate } from "@/lib/format";
import { CreditCard } from "lucide-react";
import PrintMenu from "@/components/PrintMenu";

const AGING_COLORS = {
  "0-30": "text-foreground",
  "31-60": "text-amber-600",
  "61-90": "text-orange-600",
  "+90": "text-destructive",
};

// Iter 45.1 — para buckets personalizados, colorear por posición del bucket dentro del key sort
function toneForBucket(bucket) {
  if (AGING_COLORS[bucket]) return AGING_COLORS[bucket];
  if (bucket?.startsWith("+")) return "text-destructive";
  const m = /^(\d+)-(\d+)$/.exec(bucket || "");
  if (!m) return "text-foreground";
  const end = Number(m[2]);
  if (end <= 30) return "text-foreground";
  if (end <= 60) return "text-amber-600";
  if (end <= 90) return "text-orange-600";
  return "text-destructive";
}

export function PaymentDialog({ open, onOpenChange, type, document: doc, onPaid }) {
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("efectivo");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);

  useEffect(() => {
    if (open && doc) {
      setAmount(doc.balance_due || 0);
      setMethod("efectivo"); setNotes("");
      setLastPayment(null);
    }
  }, [open, doc]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments", {
        type, reference_id: doc.id, amount: Number(amount),
        payment_method: method, notes,
      });
      toast.success("Pago registrado");
      setLastPayment(data);
      onPaid?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Registrar pago — {type === "receivable" ? "Cobro" : "Pago a suplidor"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-muted rounded-md p-3">
            <div className="overline">{type === "receivable" ? "Factura" : "Compra"}</div>
            <div className="font-bold data-cell">{doc.number}</div>
            <div className="grid grid-cols-3 mt-2 text-xs">
              <div><div className="overline">Total</div><div className="data-cell font-bold">{formatDOP(doc.total)}</div></div>
              <div><div className="overline">Pagado</div><div className="data-cell">{formatDOP(doc.paid_amount || 0)}</div></div>
              <div><div className="overline">Saldo</div><div className="data-cell text-primary font-bold">{formatDOP(doc.balance_due || 0)}</div></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Monto a pagar</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 font-mono" data-testid="payment-amount" />
            </div>
            <div>
              <Label className="overline">Método</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="overline">Notas</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          {lastPayment ? (
            <>
              <PrintMenu
                url={`/payments/${lastPayment.id}/pdf`}
                filename={`Recibo-${(lastPayment.id || "").slice(0, 8)}`}
                label="Imprimir recibo"
                testid="payment-receipt-print"
              />
              <Button onClick={() => onOpenChange(false)} className="bg-primary font-bold" data-testid="close-payment-btn">
                Cerrar
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button onClick={submit} disabled={submitting} className="bg-primary font-bold" data-testid="confirm-payment-btn">
                <CreditCard className="w-4 h-4 mr-1.5" /> Registrar pago
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ARAPTable({ kind, items, onSelectDoc }) {
  // kind: "receivable" | "payable"
  const isAR = kind === "receivable";
  return (
    <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid={`${kind}-table`}>
      <table className="w-full text-sm">
        <thead className="overline border-b border-border">
          <tr>
            <th className="text-left p-3">{isAR ? "Factura" : "Compra"}</th>
            <th className="text-left p-3">{isAR ? "Cliente" : "Suplidor"}</th>
            <th className="text-left p-3">Fecha</th>
            <th className="text-right p-3">Total</th>
            <th className="text-right p-3">Pagado</th>
            <th className="text-right p-3">Saldo</th>
            <th className="text-center p-3">Antigüedad</th>
            <th className="text-right p-3 w-32">Acción</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-b border-border/60 hover:bg-accent">
              <td className="p-3 data-cell font-bold">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span>{it.number}</span>
                  {isAR && it.source === "migration" && (
                    <span
                      className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30 uppercase tracking-wider"
                      title="CxC migrada desde el sistema anterior"
                      data-testid={`arap-mig-badge-${it.number}`}
                    >
                      Mig.
                    </span>
                  )}
                </div>
              </td>
              <td className="p-3">{isAR ? it.customer_name : it.supplier_name}<div className="text-xs text-muted-foreground data-cell">{isAR ? it.customer_rnc_cedula : it.supplier_rnc}</div></td>
              <td className="p-3 text-xs text-muted-foreground">{formatDate(it.created_at)}</td>
              <td className="p-3 text-right data-cell">{formatDOP(it.total)}</td>
              <td className="p-3 text-right data-cell text-success">{formatDOP(it.paid_amount)}</td>
              <td className="p-3 text-right data-cell font-bold text-primary">{formatDOP(it.balance_due)}</td>
              <td className={`p-3 text-center text-xs data-cell font-bold ${toneForBucket(it.aging_bucket)}`}>
                {it.days_overdue}d ({it.aging_bucket})
              </td>
              <td className="p-3 text-right">
                {isAR && it.source === "migration" ? (
                  <span className="text-[10px] text-muted-foreground italic" title="Los cobros de CxC migrada se registran desde el módulo CxC Migración">
                    Ir a CxC Migración
                  </span>
                ) : (
                  <Button size="sm" onClick={() => onSelectDoc(it)} className="bg-primary font-bold" data-testid={`pay-${it.number}`}>
                    Registrar pago
                  </Button>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Sin saldos pendientes</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const BUCKET_TONE = [
  "text-foreground",
  "text-amber-600",
  "text-orange-600",
  "text-destructive",
];

export function ARAPSummary({ totals, kind = "receivable", includeMigration = "true", onToggleMigration, bucketsInput, onBucketsChange }) {
  const hasSource = !!totals.by_source;
  const invAmount = hasSource ? Number(totals.by_source.invoice || 0) : 0;
  const migAmount = hasSource ? Number(totals.by_source.migration || 0) : 0;
  // Buckets dinámicos (Iter 45.1): usa `bucket_keys` del backend o fallback a los 4 clásicos
  const bucketKeys = Array.isArray(totals.bucket_keys) && totals.bucket_keys.length > 0
    ? totals.bucket_keys
    : ["0-30", "31-60", "61-90", "+90"];
  return (
    <div className="mb-4 space-y-3">
      {kind === "receivable" && onToggleMigration && (
        <div className="flex items-center justify-between bg-muted/30 border border-border rounded-md p-2.5 text-xs">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="arap-include-migration"
              className="accent-primary"
              checked={includeMigration !== "false"}
              onChange={(e) => onToggleMigration(e.target.checked ? "true" : "false")}
              data-testid="arap-include-migration-toggle"
            />
            <label htmlFor="arap-include-migration" className="cursor-pointer">
              Incluir <b>CxC migrada</b> del sistema anterior
              <span className="ml-2 text-muted-foreground">(marcada con badge <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">Mig.</span> en la tabla)</span>
            </label>
          </div>
          {hasSource && (
            <div className="text-[11px] data-cell text-muted-foreground">
              Facturas: <b className="text-foreground">{formatDOP(invAmount)}</b>
              {migAmount > 0 && (
                <span className="ml-3">Migración: <b className="text-amber-700 dark:text-amber-400">{formatDOP(migAmount)}</b></span>
              )}
            </div>
          )}
        </div>
      )}
      {/* Iter 45.1 — cortes personalizables (aplica a CxC y CxP) */}
      {onBucketsChange && (
        <div className="flex flex-wrap items-center gap-3 bg-muted/20 border border-border/60 rounded-md px-2.5 py-2 text-xs">
          <span className="overline text-[10px]">Cortes de antigüedad (días):</span>
          <input
            type="text"
            value={bucketsInput}
            onChange={(e) => onBucketsChange(e.target.value)}
            placeholder="30,60,90"
            className="h-7 w-40 border border-border rounded px-2 bg-background font-mono tabular-nums text-xs"
            data-testid={`arap-buckets-input-${kind}`}
            title="Enteros positivos separados por coma, en orden ascendente. Ej. 15,30,60,90."
          />
          <div className="flex items-center gap-1">
            {["30,60,90", "15,30,60,90", "7,15,30"].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => onBucketsChange(preset)}
                className={`px-1.5 py-0.5 rounded text-[10px] border font-mono ${
                  bucketsInput === preset ? "bg-primary/10 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"
                }`}
                data-testid={`arap-buckets-preset-${kind}-${preset}`}
              >
                {preset}
              </button>
            ))}
          </div>
          <span className="text-[10px] text-muted-foreground">Se ordenan automáticamente.</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3" style={{ gridTemplateColumns: `1fr repeat(${bucketKeys.length}, minmax(0, 1fr))` }}>
        <div className="kpi-card border-primary">
          <div className="overline text-primary">Total pendiente</div>
          <div className="text-2xl font-black mt-2 data-cell text-primary">{formatDOP(totals.total)}</div>
          <div className="text-xs text-muted-foreground mt-1">{totals.count} documentos</div>
        </div>
        {bucketKeys.map((key, i) => {
          const tone = BUCKET_TONE[Math.min(i, BUCKET_TONE.length - 1)];
          const label = key.startsWith("+") ? `${key.slice(1)}+ días` : `${key} días`;
          return (
            <div key={key} className="kpi-card">
              <div className={`overline ${i > 0 ? tone : ""}`}>{label}</div>
              <div className={`text-xl font-black mt-2 data-cell ${i > 0 ? tone : ""}`}>{formatDOP(totals[key] || 0)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
